import 'setimmediate'; // Required for web. See: https://github.com/invertase/react-native-firebase/issues/7921#issuecomment-2239419885
import {
  DarkTheme,
  DefaultTheme,
  ThemeProvider,
} from "expo-router/react-navigation";
import { useFonts } from 'expo-font';
import { Stack } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { useEffect } from 'react';
import {
  configureReanimatedLogger,
  ReanimatedLogLevel,
} from 'react-native-reanimated';

import { useColorScheme } from '@/hooks/useColorScheme';
import CodeGate from '@/components/CodeGate';
import MobileGate from '@/components/MobileGate';
import {
  isMobileWebDevice,
  mobileGateSource,
} from '@/utils/isMobileWebDevice';
import { StoreProvider } from '@/stores/StoreProvider';
import { LogBox, Platform } from 'react-native';
import * as Sentry from '@sentry/react-native';

import { config } from '@/config';
import { getAppVersion, getAppBuildNumber } from '@/utils/appVersion';
import { isLogStreamingEnabled } from '@/services/logging/logStreaming';

const sentryDsn = config.sentry?.dsn;
const sentryEnabled =
  config.shouldInitializeSentry &&
  !!sentryDsn &&
  !sentryDsn.startsWith('REPLACE_WITH_');

if (sentryEnabled) {
  Sentry.init({
    dsn: sentryDsn,

    environment: config.env,
    release: `fit52tv@${getAppVersion() ?? 'unknown'}`,
    dist: getAppBuildNumber() ?? 'unknown',

    // Adds more context data to events (IP address, cookies, user, etc.)
    // For more information, visit: https://docs.sentry.io/platforms/react-native/data-management/data-collected/
    sendDefaultPii: true,

    // Enable Logs
    enableLogs: true,

    // Gate Logs streaming at runtime. Non-prod always streams; production only streams once an
    // authenticated user has the STREAM_TV_LOGS property (see services/logging/logStreaming.ts).
    // Read fresh per log, so it covers both console.* forwards and direct Sentry.logger.* calls.
    beforeSendLog: (log) => (isLogStreamingEnabled() ? log : null),

    // Configure Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1,

    integrations(integrations) {
      const sentryIntegrations = [
        ...integrations,
        Sentry.mobileReplayIntegration(),
        Sentry.feedbackIntegration(),
        Sentry.consoleLoggingIntegration(),
      ];
      return sentryIntegrations;
    },

    // uncomment the line below to enable Spotlight (https://spotlightjs.com)
    // spotlight: __DEV__,
  });

  Sentry.getGlobalScope().setAttributes({
    buildNumber: getAppBuildNumber() ?? 'unknown',
    platformOS: Platform.OS,
    // Whether the web phone/tablet gate tripped. Sentry already records the
    // user agent (sendDefaultPii), so this is enough to audit misclassifications
    // — i.e. desktop visitors we sent to the app store by mistake. The source
    // comes along so sessions with a forced ?mobileGate override can be excluded
    // from that audit rather than read as real detections.
    isMobileWebDevice,
    mobileGateSource,
  });
} else if (!config.shouldInitializeSentry) {
  console.log(
    `Skipping Sentry initialization: disabled for env "${config.env}"`,
  );
} else {
  console.log(
    `Skipping Sentry initialization: no DSN configured for env "${config.env}"`,
  );
}

// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync();

// Disable reanimated warnings
configureReanimatedLogger({
  level: ReanimatedLogLevel.warn,
  strict: false,
});

if (Platform.OS === 'ios') {
  console.log('Ignoring all log notifications on iOS');
  LogBox.ignoreAllLogs(true);
}

function RootLayout() {
  const colorScheme = useColorScheme();
  const [loaded, error] = useFonts({
    SpaceMono: require('../assets/fonts/SpaceMono-Regular.ttf'),
  });

  useEffect(() => {
    if (loaded || error) {
      SplashScreen.hideAsync();
      if (error) {
        console.warn(`Error in loading fonts: ${error}`);
      }
    }
  }, [loaded, error]);

  if (!loaded && !error) {
    return null;
  }

  return (
    // MobileGate wraps CodeGate: a phone visitor should be pointed at the app,
    // not asked for an unlock code they have no reason to enter.
    <MobileGate>
      <CodeGate>
        <ThemeProvider
          value={colorScheme === 'dark' ? DarkTheme : DefaultTheme}
        >
          <StoreProvider>
            <Stack>
              <Stack.Screen name="(main)" options={{ headerShown: false }} />
              <Stack.Screen
                name="(workout)"
                options={{
                  headerShown: false,
                  animation: 'none',
                }}
              />
              <Stack.Screen name="debug" options={{ headerShown: false }} />
              <Stack.Screen name="+not-found" />
            </Stack>
          </StoreProvider>
        </ThemeProvider>
      </CodeGate>
    </MobileGate>
  );
}

// Only wrap the root layout with Sentry when the SDK was actually initialized.
export default sentryEnabled ? Sentry.wrap(RootLayout) : RootLayout;
